import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { LoggerService } from '@app/services/logger.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { SnackBarFacade, displayMessage, emptyAction, userLoginComplete } from '@ra-state';
import { ApiEndPoints, DataService } from '@services/data.service';
import { Observable, combineLatest, map, of, switchMap, throwError } from 'rxjs';
import { CommandRequestBuilderService } from 'src/app/services/command-request-builder.service';
import { EntitlementEvent, UserEvents } from 'src/app/services/command-request.service';
import {
  cancelFakeEntitlement,
  clearEntitlementGridRefresh,
  getOrganizations,
  getUserPreferences,
  refreshEntitlementGrid,
  setOrganizations,
  setSecondaryEmailsSuccess,
  setUserPreferences,
  updateSecondaryEmails,
} from './user.actions';

@Injectable({
  providedIn: 'root',
})
export class UserEffects {
  private readonly actions$ = inject(Actions);
  private readonly dataService = inject(DataService);
  private readonly commandRequestBuilderService = inject(CommandRequestBuilderService);
  private readonly snackBarFacade = inject(SnackBarFacade);
  logger = inject(LoggerService).withContext(UserEffects);

  createSpUser$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(userLoginComplete),
      switchMap(() => {
        const commandRequest = this.commandRequestBuilderService
          .new(`/SupportUsers`, 'POST', UserEvents.SpUserCreated)
          .withBody({})
          .withWaitOn201Created();
        return this.dataService.commandRequest$(commandRequest);
      }),
      map(() => emptyAction()),
    );
  });

  userPreferences$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(getUserPreferences),
      switchMap((payload) => {
        return this.dataService.getUserPreferences$(payload.userId);
      }),
      map((response) => {
        return setUserPreferences({ userState: response });
      }),
    );
  });

  secondaryEmails$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(updateSecondaryEmails),
      switchMap((payload) => {
        const commandRequest = this.commandRequestBuilderService
          .new(
            `${ApiEndPoints.CustomerSupport}${payload.userId}/secondaryEmails`,
            'PUT',
            UserEvents.UserSecondaryEmailsSet,
          )
          .withBody(payload.secondaryEmails)
          .asAssistedRequest()
          .withWaitOn202Accepted();

        return combineLatest([this.dataService.commandRequest$(commandRequest), of(payload.secondaryEmails)]);
      }),
      switchMap(([_res, secondaryEmails]) => {
        const actions: Action[] = [];
        const displayMsg = displayMessage({
          payload: {
            message: 'The secondary entitlement account was updated successfully for user',
            type: 'Success',
          },
        });
        actions.push(displayMsg, setSecondaryEmailsSuccess({ secondaryEmails }));
        return actions;
      }),
    );
  });

  organizations$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(getOrganizations),
      switchMap((payload) => {
        return this.dataService.getUserOrganisations$(payload.userId);
      }),
      map((response) => {
        return setOrganizations({ organizations: response });
      }),
    );
  });

  cancelFakeEntitlement$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(cancelFakeEntitlement),
      switchMap((payload) => {
        const commandRequest = this.commandRequestBuilderService
          .new(
            `${ApiEndPoints.Users}entitlements/${payload.entitlementId}?action=Cancelled`,
            'DELETE',
            EntitlementEvent.EntitlementCancelledV2,
          )
          .withWaitOn200Ok()
          .withErrorHandler((error: unknown) => {
            const httpError = error as HttpErrorResponse;
            if (httpError.error) {
              this.snackBarFacade.displayMessage({
                message:
                  'Something went wrong when trying to cancel the fake entitlement. Please refresh and try again.',
                type: 'Error',
                title: '',
              });
            }
            return throwError(() => error);
          });
        return this.dataService.commandRequest$(commandRequest);
      }),
      switchMap((_res) => {
        return [
          displayMessage({
            payload: {
              message: 'Entitlement cancelled successfully.',
              type: 'Success',
            },
          }),
          refreshEntitlementGrid(),
          clearEntitlementGridRefresh(),
        ];
      }),
    );
  });
}
